import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Link,
  Stack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { createFileCopy, formatFileSize } from '../../utils/functions';
import { FiFile, FiTrash } from 'react-icons/fi';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';
import { IDateType } from '../../services/hooks/Types';
import { useEffect, useMemo } from 'react';

export interface IAttachmentSpecialCondition {
  id: string;
  name: string;
  url: string;
  created_at?: IDateType;
}

interface IOwnProps {
  index: number;
  onDeleteAttachment(id: string): void;
  sentAttachments?: IAttachmentSpecialCondition[];
  inputName?: string;
  buttonText?: string;
  multiple?: boolean;
}

const AttachmentButtonFile: React.FC<IOwnProps> = ({
  index,
  sentAttachments = [],
  inputName = 'attachmentFile',
  buttonText = 'Selecionar arquivo',
  multiple = true,
  onDeleteAttachment,
}) => {
  const { setValue, watch, register, formState } = useFormContext<FieldValues>();

  const attachments = watch(inputName) || [];
  const fileList: File[] = Array.isArray(attachments[index]) ? attachments[index] : [];

  const previewUrls = useMemo(() => fileList.map((f) => ({ file: f, url: URL.createObjectURL(f) })), [fileList]);

  useEffect(() => {
    return () => {
      previewUrls.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [previewUrls]);

  const removeFile = (idx: number) => {
    const newFiles = fileList.filter((_, i) => i !== idx);
    setValue(`${inputName}.${index}`, newFiles, { shouldValidate: true });
  };

  const fieldErrors = formState.errors?.[inputName] as FieldErrors[] | undefined;

  return (
    <Box>
      <Input
        type="file"
        id={`file-${inputName}-${index}`}
        display="none"
        {...register(`${inputName}.${index}`, {
          onChange: async (file: React.ChangeEvent<HTMLInputElement>) => {
            file.preventDefault();
            if (!file.target || file.target.files?.length === 0 || !file.target.files) return;

            try {
              const selectedFile = file.target.files[0];
              if (!selectedFile) return;
              const fileCopy = await createFileCopy(selectedFile);

              setValue(`${inputName}.${index}`, multiple ? [...fileList, fileCopy] : [fileCopy], {
                shouldValidate: true,
                shouldDirty: true,
              });
            } finally {
              file.target.value = '';
            }
          },
        })}
        // onChange={(e) => {
        //   const files = e.target.files ? Array.from(e.target.files) : [];
        //   setValue(`${inputName}.${index}`, files, { shouldValidate: true });
        // }}
      />
      <Stack justify="center" gap={2}>
        <Button
          as="label"
          htmlFor={`file-${inputName}-${index}`}
          size="sm"
          w="100%"
          maxW={['100%', '100%', '50%']}
          colorScheme="blue"
          cursor="pointer"
        >
          {buttonText}
        </Button>
        {multiple && (
          <Text fontSize="xs" color="gray.500">
            Selecione um arquivo por vez; você pode adicionar os demais em seguida.
          </Text>
        )}

        {(fileList.length > 0 || sentAttachments.length > 0) && (
          <VStack align="start" spacing={1} my={1} w="full">
            {fileList.map((file, idx) => {
              const preview = previewUrls[idx]?.url;

              return (
                <HStack key={idx} w="full" justify="space-between" p={2} bg="white" rounded="md" shadow="xs">
                  <Link href={preview} target="_blank" color="blue.600" w="full">
                    <HStack spacing={2}>
                      <Icon as={FiFile} color="blue.400" />
                      <Stack spacing={0}>
                        <Tooltip label={file.name} maxW="200px">
                          <Text fontSize="xs" noOfLines={1} maxW="200px">
                            {file.name}
                          </Text>
                        </Tooltip>
                        <Text fontSize="0.6rem" color="gray">
                          Tamanho: {formatFileSize(file.size)}
                        </Text>
                      </Stack>
                    </HStack>
                  </Link>
                  <Icon as={FiTrash} color="red.400" cursor="pointer" onClick={() => removeFile(idx)} />
                </HStack>
              );
            })}

            {sentAttachments.map((file) => (
              <HStack key={file.id} w="full" justify="space-between" p={2} bg="#e8fde9" rounded="md" shadow="xs">
                <Link href={file.url} target="_blank" color="blue.600" w="full">
                  <HStack spacing={2}>
                    <Icon as={FiFile} color="blue.400" />
                    <Stack spacing={0}>
                      <Tooltip label={file.name} maxW="200px">
                        <Text fontSize="xs" noOfLines={1} maxW="200px">
                          {file.name}
                        </Text>
                      </Tooltip>
                      <Text fontSize="0.6rem" color="gray">
                        Enviado em: {file.created_at?.br}
                      </Text>
                    </Stack>
                  </HStack>
                </Link>
                {file.id && sentAttachments.length > 1 && (
                  <Icon as={FiTrash} color="red.400" cursor="pointer" onClick={() => onDeleteAttachment(file.id)} />
                )}
              </HStack>
            ))}
            {sentAttachments.length === 1 && (
              <Alert status="info" p={2} fontSize="0.73rem" rounded="md">
                <AlertIcon />
                Para remover este arquivo, é necessario anexar um novo arquivo.
              </Alert>
            )}
          </VStack>
        )}
      </Stack>

      {fieldErrors?.[index] && (
        <Text mt={1} color="#E53E3E">
          {fieldErrors[index]?.message?.toString()}
        </Text>
      )}
    </Box>
  );
};

export default AttachmentButtonFile;
